// SCR-STAFF-312 — گزارش نوبت‌دهی (US-SCH-090): تعداد به تفکیک وضعیت، نرخ لغو (زودهنگام/دیرهنگام) و غیبت، بهره‌وری (زمان رزروشده
// به زمان در دسترس)، میانگین فاصله رزرو تا نوبت و انتظار، علل لغو، سهم کانال و زمان‌های آزادشده پرشده؛ تفکیک ارائه‌دهنده، خدمت،
// شعبه، کانال یا روز و بازه تاریخ.
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { unwrap, type AppointmentReportRow } from '@phoenix/api-client';
import { useTenantSession } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import { DataTable, ErrorState, faDigits, ListPage, LoadingState, type DataTableColumn } from '@phoenix/ui';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { formatJalaliInput, parseJalaliInput } from '../patients/common';
import { addDays, percent, schKeys, useActiveBranch, useBookingOptions } from './common';

export const SCR_STAFF_312 = 'SCR-STAFF-312';

const GROUPS = ['provider', 'service', 'branch', 'channel', 'day'] as const;

function Metric({ labelKey, value, testId }: { labelKey: string; value: string; testId: string }) {
  const { t } = useTranslation('sch');
  return (
    <Card variant="outlined" sx={{ minWidth: 150, flex: 1 }}>
      <CardContent>
        <Typography variant="caption" color="text.secondary">
          {t(labelKey)}
        </Typography>
        <Typography variant="h6" component="p" data-testid={testId}>
          {value}
        </Typography>
      </CardContent>
    </Card>
  );
}

function decimal(value: number | string | null | undefined): string {
  return value === null || value === undefined ? '—' : faDigits(Math.round(Number(value) * 10) / 10);
}

export function AppointmentReportPage() {
  const { t } = useTranslation('sch');
  const { tenantApi, session } = useTenantSession();
  const { branchId } = useActiveBranch();
  const today = useBookingOptions(branchId).data?.today ?? null;
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [groupBy, setGroupBy] = useState<(typeof GROUPS)[number]>('provider');

  useEffect(() => {
    if (today && from === '' && to === '') {
      setFrom(formatJalaliInput(addDays(today, -30)));
      setTo(formatJalaliInput(today));
    }
  }, [today, from, to]);

  const fromDate = parseJalaliInput(from);
  const toDate = parseJalaliInput(to);
  const report = useQuery({
    queryKey: schKeys.report(session?.tenantId, `${fromDate}|${toDate}|${groupBy}|${branchId}`),
    queryFn: () =>
      unwrap(
        tenantApi.GET('/api/v1/scheduling/reports/appointments', {
          params: { query: { from: fromDate ?? undefined, to: toDate ?? undefined, groupBy, branchId: branchId ?? undefined } },
        }),
      ),
    enabled: fromDate !== null && toDate !== null,
    placeholderData: keepPreviousData,
  });
  const summary = report.data?.summary;
  const columns: DataTableColumn<AppointmentReportRow>[] = [
    {
      id: 'label',
      headerKey: 'sch:report.columns.label',
      cell: (row) => (groupBy === 'channel' ? t(`sch:channel.${row.key}`, { defaultValue: row.label ?? row.key }) : (row.label ?? row.key)),
    },
    { id: 'total', headerKey: 'sch:report.columns.total', cell: (row) => faDigits(Number(row.total)) },
    { id: 'cancel', headerKey: 'sch:report.columns.cancel_rate', cell: (row) => percent(row.cancelRate) },
    { id: 'late', headerKey: 'sch:report.columns.late_cancel_rate', cell: (row) => percent(row.lateCancelRate) },
    { id: 'noShow', headerKey: 'sch:report.columns.no_show_rate', cell: (row) => percent(row.noShowRate) },
    { id: 'utilization', headerKey: 'sch:report.columns.utilization', cell: (row) => percent(row.utilization) },
    { id: 'lead', headerKey: 'sch:report.columns.lead_days', cell: (row) => decimal(row.averageLeadDays) },
    { id: 'wait', headerKey: 'sch:report.columns.wait_minutes', cell: (row) => decimal(row.averageWaitMinutes) },
  ];
  return (
    <ListPage
      screenId={SCR_STAFF_312}
      titleKey="sch:report.title"
      subtitleKey="sch:report.subtitle"
      filters={
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <TextField
            size="small"
            label={t('sch:report.from')}
            value={from}
            onChange={(event) => setFrom(event.target.value)}
            placeholder={t('sch:schedules.date_placeholder')}
            inputProps={{ dir: 'ltr', 'data-testid': 'report-from' }}
          />
          <TextField
            size="small"
            label={t('sch:report.to')}
            value={to}
            onChange={(event) => setTo(event.target.value)}
            placeholder={t('sch:schedules.date_placeholder')}
            inputProps={{ dir: 'ltr', 'data-testid': 'report-to' }}
          />
          <TextField
            select
            size="small"
            label={t('sch:report.group_by')}
            value={groupBy}
            onChange={(event) => setGroupBy(event.target.value as (typeof GROUPS)[number])}
            sx={{ minWidth: 160 }}
            inputProps={{ 'data-testid': 'report-group-by' }}
          >
            {GROUPS.map((group) => (
              <MenuItem key={group} value={group}>
                {t(`sch:report.group.${group}`)}
              </MenuItem>
            ))}
          </TextField>
        </Stack>
      }
    >
      {report.error ? <ErrorState error={report.error} onRetry={() => void report.refetch()} /> : null}
      {report.isPending && fromDate !== null ? <LoadingState rows={3} /> : null}
      {summary && report.data ? (
        <Stack spacing={2}>
          <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 2 }}>
            <Metric labelKey="sch:report.columns.total" value={faDigits(Number(summary.total))} testId="report-total" />
            <Metric labelKey="sch:report.columns.cancel_rate" value={percent(summary.cancelRate)} testId="report-cancel-rate" />
            <Metric labelKey="sch:report.columns.late_cancel_rate" value={percent(summary.lateCancelRate)} testId="report-late-cancel-rate" />
            <Metric labelKey="sch:report.columns.no_show_rate" value={percent(summary.noShowRate)} testId="report-no-show-rate" />
            <Metric labelKey="sch:report.columns.utilization" value={percent(summary.utilization)} testId="report-utilization" />
            <Metric labelKey="sch:report.columns.lead_days" value={decimal(summary.averageLeadDays)} testId="report-lead" />
            <Metric labelKey="sch:report.columns.wait_minutes" value={decimal(summary.averageWaitMinutes)} testId="report-wait" />
            <Metric
              labelKey="sch:report.freed_filled"
              value={`${faDigits(Number(report.data.freedSlotsFilled))} / ${faDigits(Number(report.data.freedSlots))}`}
              testId="report-freed"
            />
          </Stack>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            <Typography variant="body2" sx={{ flex: 1 }} data-testid="report-statuses">
              {t('sch:report.by_status')}:{' '}
              {summary.byStatus.map((item) => `${t(`sch:status.${item.status}`)} ${faDigits(Number(item.count))}`).join(' · ')}
            </Typography>
            <Typography variant="body2" sx={{ flex: 1 }} data-testid="report-channels">
              {t('sch:report.channels')}:{' '}
              {report.data.channels.map((item) => `${t(`sch:channel.${item.channel}`)} ${percent(item.share)}`).join(' · ')}
            </Typography>
            <Typography variant="body2" sx={{ flex: 1 }} data-testid="report-reasons">
              {t('sch:report.cancel_reasons')}:{' '}
              {report.data.cancelReasons
                .map((item) => `${t(`sch:cancel_reason.${item.code}`, { defaultValue: item.code })} ${faDigits(Number(item.count))}`)
                .join(' · ') || '—'}
            </Typography>
          </Stack>
          <DataTable
            captionKey="sch:report.title"
            columns={columns}
            rows={report.data.rows}
            getRowId={(row) => row.key}
            emptyTitleKey="sch:report.empty"
          />
        </Stack>
      ) : null}
    </ListPage>
  );
}
